import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { subscribeToUserLists, getUserLists } from '../firebase/database';

const ListsContext = createContext();

export const useLists = () => {
  const context = useContext(ListsContext);
  if (!context) {
    throw new Error('useLists must be used within a ListsProvider');
  }
  return context;
};

export const ListsProvider = ({ children }) => {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) {
      setLists([]);
      setLoading(false);
      return;
    }

    const unsubscribe = subscribeToUserLists(user.uid, (userLists) => {
      setLists(userLists);
      setLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, [user]);

  // Function to refresh lists manually (useful after joining a shared list)
  const refreshLists = useCallback(async () => {
    if (!user) {
      return;
    }
    
    setLoading(true);
    try {
      const result = await getUserLists(user.uid);
      if (result.success) {
        setLists(result.data);
      } else {
        console.error('Failed to refresh lists:', result.error);
      }
    } catch (error) {
      console.error('Error refreshing lists:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Function to force refresh the subscription
  const forceRefresh = useCallback(() => {
    refreshLists();
  }, [refreshLists]);

  const value = {
    lists,
    loading,
    setLists,
    refreshLists,
    forceRefresh
  };

  return (
    <ListsContext.Provider value={value}>
      {children}
    </ListsContext.Provider>
  );
};
